// Local wire types for the store-configuration half of Settings (profile,
// hours & closures, ordering, tables, POS) — hand-authored against
// server/src/models/{roots,trading,settings,dine_in_delivery}.rs, the shapes
// the testbed's config_store scenario proves. No code is shared with the
// server: the contract is the document.

import type { Store } from '../../lib/api';

export type { Store };

/** `PUT /stores/{sid}` replaces the whole profile — every field, every time. */
export type StoreRequest = Omit<Store, 'restaurantId' | 'storeId'>;

export const storeRequest = (s: Store): StoreRequest => ({
  name: s.name,
  timezone: s.timezone,
  address: s.address,
  phoneE164: s.phoneE164,
  pickupEnabled: s.pickupEnabled,
  deliveryEnabled: s.deliveryEnabled,
  dineInEnabled: s.dineInEnabled,
  temporarilyClosed: s.temporarilyClosed,
  permanentlyClosed: s.permanentlyClosed,
  dayCloseTime: s.dayCloseTime,
});

/** `GET /stores/{sid}/public` — what an ordering client sees signed out. */
export type StorePublic = {
  restaurantId: string;
  storeId: string;
  name: string;
  timezone: string;
  address: string;
  phoneE164: string;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  dineInEnabled: boolean;
  temporarilyClosed: boolean;
  permanentlyClosed: boolean;
  onlinePaymentsEnabled: boolean;
};

// ── 55-trading: hours, holidays, pause, surcharge rules ─────────────

export type HoursScope = 'pos' | 'pickup' | 'delivery' | 'dine_in';

/** One service window: ISO day 1–7, session 1 | 2, store-local HH:MM when
 * open. close < open spills past midnight. */
export type HoursWindow = {
  dayOfWeek: number;
  sessionNo: number;
  isOpen: boolean;
  openTime: string | null;
  closeTime: string | null;
};

export type ScopeHours = { scope: HoursScope; windows: HoursWindow[]; openNow: boolean };

export type HoursView = { timezone: string; paused: boolean; scopes: ScopeHours[] };

export type Holiday = { holidayDate: string; label: string; isClosed: boolean };
export type HolidayRequest = { label: string; isClosed: boolean };

export type SurchargeType = 'credit_card' | 'eftpos' | 'public_holiday' | 'custom';

/** A rate (basis points, 1–10000) or a positive amount — exactly one. */
export type SurchargeRule = {
  surchargeId: string;
  surchargeType: SurchargeType;
  label: string;
  percentBp: number | null;
  amountInCents: number | null;
  appliesBelowInCents: number | null;
  appliesPos: boolean;
  appliesPickup: boolean;
  appliesDelivery: boolean;
  appliesDineIn: boolean;
  active: boolean;
  sortOrder: string;
  createdAt: string;
  updatedAt: string;
};

export type SurchargeRuleRequest = Omit<SurchargeRule, 'surchargeId' | 'sortOrder' | 'createdAt' | 'updatedAt'>;

// ── 75-store-settings: one document, written per section ────────────

export type ServiceTimes = {
  pickupLeadMinutes: number;
  deliveryLeadMinutes: number;
  dineInLeadMinutes: number;
  scheduleAheadDays: number;
  autoAcceptPickup: boolean;
  autoAcceptDelivery: boolean;
  autoAcceptDineIn: boolean;
  autoAcceptEtaMinutes: number | null;
};

export type ReceiptSettings = {
  headerText: string | null;
  footerText: string | null;
  showStoreLogo: boolean;
  showCustomerInfo: boolean;
  showTableInfo: boolean;
  showGstBreakdown: boolean;
};

export type OrderAlertSettings = {
  ringNewOrders: boolean;
  repeatAlert: boolean;
  repeatIntervalSeconds: number | null;
  unprocessedAlertMinutes: number | null;
  alertEmail: string | null;
};

export type DineInSettings = {
  qrOrderingEnabled: boolean;
  requireTableSelection: boolean;
  promptGuestCount: boolean;
  payAtStoreAllowed: boolean;
};

export type EtaOption = { etaOptionId: string; minutes: number; label: string | null; sortOrder: string };
export type EtaOptionRequest = { minutes: number; label: string | null };

export type PosUpdateChannel = 'stable' | 'beta';

/** `GET /settings` — every section at once; absent rows read as defaults. */
export type SettingsBundle = {
  serviceTimes: ServiceTimes;
  receipt: ReceiptSettings;
  orderAlerts: OrderAlertSettings;
  dineIn: DineInSettings;
  etaOptions: EtaOption[];
  posUpdateChannel: PosUpdateChannel;
};

// ── 65-dine-in-delivery: tables, floor plan, delivery areas ─────────

export type DiningTable = {
  tableId: string;
  name: string;
  maxGuests: number | null;
  floorElementId: string | null;
  internalNotes: string | null;
  active: boolean;
  /** derived: an open dine-in order sits at this table */
  occupied: boolean;
  createdAt: string;
  updatedAt: string;
};

export type DiningTableRequest = {
  name: string;
  maxGuests: number | null;
  floorElementId: string | null;
  internalNotes: string | null;
  active: boolean;
};

/** The layout is the client's document, stored and echoed as written. */
export type FloorPlan = { layout: unknown; updatedAt: string };

export type DeliveryArea = {
  areaId: string;
  name: string;
  polygonWkt: string;
  deliveryChargeInCents: number;
  minOrderInCents: number | null;
  applyThirdPartyFee: boolean;
  sortOrder: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type DeliveryAreaRequest = {
  name: string;
  polygonWkt: string;
  deliveryChargeInCents: number;
  minOrderInCents: number | null;
  applyThirdPartyFee: boolean;
  active: boolean;
};

/** `POST /delivery-areas/match` — the zone a point falls in; a point outside
 * every active zone is the typed refusal `outside_delivery_area`. */
export type DeliveryMatch = {
  areaId: string;
  name: string;
  deliveryChargeInCents: number;
  minOrderInCents: number | null;
};

/** Rank order is bytewise — never localeCompare on sortOrder. */
export const byRank = <T extends { sortOrder: string }>(a: T, b: T): number =>
  a.sortOrder < b.sortOrder ? -1 : a.sortOrder > b.sortOrder ? 1 : 0;
